import './priceDataGrid.css'

import { Box } from '@mui/material'
import type { GridRowParams } from '@mui/x-data-grid'
import type { DateTime } from 'luxon'
import type { FC } from 'react'
import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import CustomDataGrid from '@/components/dataDisplay/CustomDataGrid'
import { useAlertContext } from '@/contexts/AlertContext'
import BiddingPricesDialog from '@/features/bidding/components/price/offer/BiddingPricesDialog'
import PriceDataGridToolbar from '@/features/bidding/components/price/offer/PriceDataGridToolbar'
import { BidType, MAX_PTU_ROWS_PER_DATAGRID } from '@/features/bidding/constants'
import { useBiddingContext } from '@/features/bidding/contexts/BiddingContext'
import { useResetPtusContext } from '@/features/bidding/contexts/ResetPtusContext'
import { usePtuExpiration } from '@/features/bidding/hooks/usePtuExpiration'
import { BulkFillMode } from '@/features/bidding/types/bid'
import { decimalAdjust } from '@/features/bidding/utils/calculations/decimalAdjust'
import type { ParsedPriceBid } from '@/features/bidding/utils/clipboard'
import { parsePriceBid } from '@/features/bidding/utils/clipboard'
import { getByCountry } from '@/features/bidding/utils/getBidConfig'
import type { GroupedBidParams } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'
import type { AggregatedBid, AggregatedBidPtu } from '@/features/bidding/utils/model/buildAggregatedBid'
import type { AggregatedBidWrapper } from '@/features/bidding/utils/model/mapActivationGroupBidsToAggregatedBids'
import { getPriceBidDataGridColumns } from '@/features/bidding/utils/price/getPriceBiddingDataGridColumns'
import type { PriceQuantity } from '@/features/bidding/utils/price/getPtuPriceQuantitiesFromBids'
import {
  calculateTotalOffered,
  createAggregatedBidFromParsedBidAndMakeVolumeInCurrentPriceColumnsAsZero,
  createAggregatedBidFromParsedBidAndRemoveCurrentPriceColumns,
  getUniquePrices,
  recalculateRemaining,
} from '@/features/bidding/utils/price/pastedBid'
import { isPortfolioEntelios } from '@/features/bidding/utils/validation/enteliosValidationUtils'

type Props = {
  aggregatedBid: AggregatedBid
  // List of activation groups that are part of the aggregated bid
  activationGroupUuids?: string[]
  bidsPerActivationGroup?: Map<string, AggregatedBidWrapper>
  groupParams: GroupedBidParams
  hasAlreadyBidsForTheDay?: boolean
  isLoading: boolean
  onAggregatedBidChange: (bid: AggregatedBid) => void
}

const norwayDirectPortfolioCodes = new Set(['NO1-A', 'NO2-A', 'NO3-A', 'NO4-A', 'NO5-A'])

const PriceDataGrid: FC<Props> = memo(function PriceDataGrid({
  aggregatedBid,
  activationGroupUuids = [],
  bidsPerActivationGroup = undefined,
  groupParams,
  hasAlreadyBidsForTheDay = false,
  isLoading,
  onAggregatedBidChange,
}) {
  const { selectedCountry } = useBiddingContext()
  const { isPtuMarkedAsResetForActivationGroup } = useResetPtusContext()
  const { t, i18n } = useTranslation()
  const { pushAlert } = useAlertContext()
  const { marketsThatRequirePositiveIntegerVolume } = getByCountry(selectedCountry)
  const { isPtuEditable, getExpirationThreshold, expirationMinutes } = usePtuExpiration()
  const [priceColumnsModalOpen, setPriceColumnsModalOpen] = useState(false)

  const isMfrrUpOrDown = groupParams.marketProgram === 'MFRR_UP' || groupParams.marketProgram === 'MFRR_DOWN'
  const isFinland = selectedCountry === 'fi'
  const isNorwayDirect = selectedCountry === 'no' && norwayDirectPortfolioCodes.has(groupParams.portfolioCode)
  const isEnergyBid = groupParams.bidType === BidType.ENERGY
  const cannotEditPriceColumns =
    hasAlreadyBidsForTheDay &&
    isMfrrUpOrDown &&
    (isFinland || isNorwayDirect || isPortfolioEntelios(groupParams.portfolioCode)) &&
    isEnergyBid
  const acceptsOnlyPositiveInteger = marketsThatRequirePositiveIntegerVolume.includes(groupParams.marketProgram)

  // Determine the next PTU to expire (only for mFRR ENERGY bids and only when it is within the expiration window)
  const expirationThreshold = getExpirationThreshold(groupParams.bidType, groupParams.marketProgram)

  let nextPtuToExpire: DateTime | null = null

  if (expirationThreshold) {
    const upcomingPtus = aggregatedBid.ptus
      .filter((p) => p.ptuStart >= expirationThreshold) // Only PTUs still editable
      .sort((a, b) => a.ptuStart.toMillis() - b.ptuStart.toMillis())

    if (upcomingPtus.length > 0) {
      const firstUpcomingPtu = upcomingPtus[0]
      const diffMinutes = firstUpcomingPtu.ptuStart.diff(expirationThreshold, 'minutes').minutes ?? 0

      // Highlight only if the candidate will expire within the configured expiration window
      if (diffMinutes <= expirationMinutes) {
        nextPtuToExpire = firstUpcomingPtu.ptuStart
      }
    }
  }

  // Assign row classes for styling
  const getRowClassName = (params: GridRowParams) => {
    const ptuRow = params.row as AggregatedBidPtu
    const ptuStart = ptuRow.ptuStart
    const editable = isPtuEditable(ptuStart, groupParams.bidType, groupParams.marketProgram)

    // Check if this PTU was reset
    const wasReset = activationGroupUuids.some((ag) => isPtuMarkedAsResetForActivationGroup(ag, ptuStart))
    if (wasReset) {
      return 'ptu-reset-row'
    }

    if (!editable) {
      return 'ptu-disabled-row'
    }

    if (nextPtuToExpire && ptuStart.equals(nextPtuToExpire)) {
      return 'ptu-about-to-be-disabled-row'
    }

    return ''
  }

  // Styles for disabled and warning rows
  const dataGridStyles = {
    maxHeight: 'calc(100vh - 300px)',
    '& .ptu-reset-row': {
      backgroundColor: '#ffebee !important', // light red
      '&:hover': {
        backgroundColor: '#ffcdd2 !important', // slightly darker red on hover
      },
    },
    '& .ptu-disabled-row': {
      backgroundColor: '#f5f5f5 !important', // light grey
      cursor: 'not-allowed',
      '&:hover': {
        backgroundColor: '#f5f5f5 !important',
      },
    },
    '& .ptu-about-to-be-disabled-row': {
      backgroundColor: '#fff9c4 !important', // light yellow
      '&:hover': {
        backgroundColor: '#fff9c4 !important',
      },
    },
    '& .ptu-error-row': {
      backgroundColor: '#f77272 !important', // red
      '&:hover': {
        backgroundColor: '#f77272 !important',
      },
    },
  }

  const parseClipboardContent = (clipboardContent: string): ParsedPriceBid => {
    let parsed: ParsedPriceBid = undefined
    try {
      parsed = parsePriceBid(clipboardContent)
    } catch (e: any) {
      pushAlert({
        message: e.message,
        severity: 'error',
      })
      return
    }
    return parsed
  }

  useEffect(() => {
    const handlePaste = (event: ClipboardEvent) => {
      const clipboardContent = event?.clipboardData?.getData('text')
      if (!clipboardContent) {
        return
      }

      if (clipboardContent.split('\n').length === 1) {
        return
      }

      const parsed = parseClipboardContent(clipboardContent)
      if (!parsed) {
        return
      }

      if (parsed.volumes.length !== aggregatedBid.ptus.length) {
        pushAlert({
          message: t('bidding.create_bid.ptus_pasted_error', {
            expected: aggregatedBid.ptus.length,
            given: parsed.volumes.length,
          }),
          severity: 'error',
        })
        return
      }

      // In case there is already a bid for the day, we need to keep the current price columns
      const handleCreateAggregateBidFromParsed = cannotEditPriceColumns
        ? createAggregatedBidFromParsedBidAndMakeVolumeInCurrentPriceColumnsAsZero
        : createAggregatedBidFromParsedBidAndRemoveCurrentPriceColumns
      const newAggregatedBid: AggregatedBid = handleCreateAggregateBidFromParsed(parsed, aggregatedBid)

      // If accepts only positive integer, we need to round down the quantities
      if (acceptsOnlyPositiveInteger) {
        for (const ptu of newAggregatedBid.ptus) {
          // Only process editable PTUs
          if (isPtuEditable(ptu.ptuStart, groupParams.bidType, groupParams.marketProgram)) {
            for (const pq of ptu.priceQuantities) {
              pq.quantity = Math.floor(pq.quantity)
            }

            // Recalculate the remaining quantities
            recalculateRemaining(ptu, aggregatedBid.ladderType)
          } else {
            for (const pq of ptu.priceQuantities) {
              pq.quantity = 0
            }
            recalculateRemaining(ptu, aggregatedBid.ladderType)
          }
        }
      }

      onAggregatedBidChange(newAggregatedBid)

      pushAlert({
        message: t('bidding.create_bid.bid_copied_successfully'),
        severity: 'success',
      })
    }

    document.addEventListener('paste', handlePaste)

    return () => {
      document.removeEventListener('paste', handlePaste)
    }
  }, [aggregatedBid, cannotEditPriceColumns, acceptsOnlyPositiveInteger])

  const processRowUpdate = async (updatedPtu: AggregatedBidPtu): Promise<AggregatedBidPtu> => {
    const newAggregatedBidPtus = aggregatedBid.ptus.map((ptu) => {
      if (ptu.ptuStart === updatedPtu.ptuStart) {
        // if the user has changed the quantity for one price, the total remaining quantity must be recalculated
        return recalculateRemaining(updatedPtu, aggregatedBid.ladderType)
      } else {
        return ptu
      }
    })
    onAggregatedBidChange({ ...aggregatedBid, ptus: newAggregatedBidPtus })
    return updatedPtu
  }

  const updatePriceColumns = async (updatedPrices: Record<number, number | null>): Promise<void> => {
    const newAggregatedBidPtus = aggregatedBid.ptus.map((ptu) => {
      const newPriceQuantities: PriceQuantity[] = []
      for (const priceQuantity of ptu.priceQuantities) {
        const newPrice = updatedPrices[priceQuantity.price]
        // `0` is a valid price (e.g. a genuine €0.00 tier), not a deletion signal — only `null`
        // (set by removing a price in the dialog) means "drop this column".
        if (newPrice === null || newPrice === undefined) continue

        newPriceQuantities.push({
          price: newPrice,
          quantity: priceQuantity.quantity,
        })
      }

      // Seed a zero-quantity entry only for genuinely new price columns (added via "Add price",
      // not a rename of an existing one) so they appear in the grid. Prices that already existed
      // before this edit and that this PTU never had (shown as "-") must stay untouched rather
      // than being force-zeroed just because some other price column changed.
      //
      // `updatedPrices` maps old price -> new price, so "added" is a property of the *key*: a new
      // column is keyed by a price that did not exist before, and keeps that key even if its value
      // is edited again before saving. A rename always reuses the existing price as its key.
      // Testing the value instead would misread a rename as an addition and force-zero it.
      for (const [oldPrice, newPrice] of Object.entries(updatedPrices)) {
        const isNewlyAddedPrice = newPrice !== null && !priceColumnsBeforeEditingRef.current.includes(Number(oldPrice))
        if (!isNewlyAddedPrice) continue

        const existingPriceQuantity = newPriceQuantities.find((priceQuantity) => priceQuantity.price === newPrice)
        if (!existingPriceQuantity) {
          newPriceQuantities.push({
            price: newPrice,
            quantity: 0,
          })
        }
      }

      return recalculateRemaining({ ...ptu, priceQuantities: newPriceQuantities }, aggregatedBid.ladderType)
    })
    onAggregatedBidChange({ ...aggregatedBid, ptus: newAggregatedBidPtus })
  }

  const onBulkFillHandler = (
    value: number | undefined,
    priceColumn: number | undefined,
    bulkFillMode: BulkFillMode,
  ) => {
    if (value === undefined || priceColumn === undefined) return

    const newAggregatedBid = {
      ...aggregatedBid,
      ptus: aggregatedBid.ptus.map((ptu) => ({
        ...ptu,
        priceQuantities: ptu.priceQuantities.map((pq) => ({ ...pq })),
      })),
    }

    for (const ptu of newAggregatedBid.ptus) {
      // Skip PTUs that are not editable
      if (!isPtuEditable(ptu.ptuStart, groupParams.bidType, groupParams.marketProgram)) continue

      let quantity =
        bulkFillMode === BulkFillMode.OFFER_RATIO ? decimalAdjust('round', (ptu.available * value) / 100, -2) : value

      // If accepts only positive integer, we need to round down the quantities
      if (acceptsOnlyPositiveInteger) {
        quantity = Math.floor(quantity)
      }

      // A PTU may carry no entry for this price yet — a never-offered window renders as "-" for
      // every column. Filling such a cell has to add an entry, otherwise the fill throws.
      const priceQuantity = ptu.priceQuantities.find((priceQuantity) => priceQuantity.price == priceColumn)
      if (priceQuantity) {
        priceQuantity.quantity = quantity
      } else {
        ptu.priceQuantities.push({ price: priceColumn, quantity })
      }

      const offered = calculateTotalOffered(ptu.priceQuantities, aggregatedBid.ladderType)
      ptu.remaining = decimalAdjust('round', ptu.available - offered, -2)
    }
    onAggregatedBidChange(newAggregatedBid)
  }

  // Snapshot of the price columns as they stood when the dialog was opened
  const priceColumnsBeforeEditingRef = useRef<number[]>([])

  // The price columns as they stand right now, in contrast to the snapshot above.
  const priceColumns = useMemo(() => getUniquePrices(aggregatedBid.ptus), [aggregatedBid.ptus])

  const columns = useMemo(
    () =>
      getPriceBidDataGridColumns({
        aggregatedBid,
        activationGroupUuids,
        bidsPerActivationGroup,
        acceptsOnlyPositiveInteger,
        editable: true,
        locale: i18n.language,
        t,
        isPtuEditable,
        isPtuMarkedAsResetForActivationGroup,
        bidType: groupParams.bidType,
        marketProgram: groupParams.marketProgram,
        portfolioCode: groupParams.portfolioCode,
        countryCode: selectedCountry,
        nextPtuToExpire,
      }),
    [
      aggregatedBid,
      acceptsOnlyPositiveInteger,
      isPtuEditable,
      groupParams.bidType,
      groupParams.marketProgram,
      getExpirationThreshold,
      nextPtuToExpire,
      i18n.language,
      t,
    ],
  )

  return (
    <Box>
      <CustomDataGrid
        disableDefaultHeaderBackground
        disableVirtualization
        hideFooter
        autoHeight={false}
        columns={columns}
        getRowClassName={getRowClassName}
        getRowId={(row: AggregatedBidPtu) => row.ptuStart.toString()}
        initialState={{
          pinnedColumns: { left: ['ptuStart'] },
          pagination: {
            paginationModel: {
              pageSize: MAX_PTU_ROWS_PER_DATAGRID,
              page: 0,
            },
          },
        }}
        isCellEditable={(params) => isPtuEditable(params.row.ptuStart, groupParams.bidType, groupParams.marketProgram)}
        isLoading={isLoading}
        processRowUpdate={processRowUpdate}
        rows={aggregatedBid.ptus}
        slotProps={{
          toolbar: {
            acceptsOnlyPositiveInteger,
            deliveryDay: groupParams.deliveryDay,
            onFill: onBulkFillHandler,
            onSetPriceColumnsClick: () => {
              priceColumnsBeforeEditingRef.current = priceColumns
              setPriceColumnsModalOpen(true)
            },
            priceColumns,
            unit: aggregatedBid.unit,
          },
        }}
        slots={{ toolbar: PriceDataGridToolbar }}
        sx={dataGridStyles}
      />

      {priceColumnsModalOpen && (
        <BiddingPricesDialog
          allPrices={priceColumns}
          disableManipulatePriceBox={cannotEditPriceColumns}
          locales={i18n.language}
          open={priceColumnsModalOpen}
          priceColumnsBeforeEditing={priceColumnsBeforeEditingRef.current}
          onClose={() => setPriceColumnsModalOpen(false)}
          onSubmit={updatePriceColumns}
        />
      )}
    </Box>
  )
})

export default PriceDataGrid
